import React, { useEffect, useMemo, useState } from 'react';
import { Search, LayoutGrid, List, Rows3, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import BrandCard from '../components/BrandCard';
import CategorySectionHeader from '../components/CategorySectionHeader';
import api, { absoluteAsset } from '../services/api';
import { sortCategories } from '../services/sort';

const VIEWS = [
  { key: 'grid', icon: LayoutGrid, label: 'Cuadrícula' },
  { key: 'compact', icon: Rows3, label: 'Compacta' },
  { key: 'list', icon: List, label: 'Lista' }
];

const Home = () => {
  const [query, setQuery] = useState('');
  const [view, setView] = useState('grid');
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    Promise.all([api.get('/brands'), api.get('/categories')])
      .then(([b, c]) => {
        if (!on) return;
        setBrands(b.data.map(x => ({ ...x, logo: absoluteAsset(x.logo) })));
        setCategories(sortCategories(c.data));
      })
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  const filteredByCat = useMemo(() => {
    const q = query.trim().toLowerCase();
    return categories.map(cat => ({
      ...cat,
      brands: brands
        .filter(b => b.category === cat.name)
        .filter(b => !q || b.name.toLowerCase().includes(q))
    }));
  }, [query, brands, categories]);

  const gridClass = view === 'grid'
    ? 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
    : view === 'compact'
    ? 'grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
    : 'grid grid-cols-1 gap-3 md:grid-cols-2';

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900">Marcas</h1>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar marcas..."
              className="h-11 w-full rounded-full border-slate-200 bg-white/80 pl-10 pr-4 text-slate-800 placeholder:text-slate-400 focus-visible:ring-emerald-500 sm:w-80"
            />
          </div>
          <div className="flex h-11 items-center gap-1 rounded-full border border-slate-200 bg-white/80 p-1">
            {VIEWS.map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                aria-label={label}
                title={label}
                className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                  view === key
                    ? 'bg-emerald-200/70 text-emerald-900'
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
      ) : (
        <div className="mt-10 space-y-12">
          {filteredByCat.map(cat => (
            cat.brands.length > 0 && (
              <section key={cat.id}>
                <CategorySectionHeader title={cat.name} count={cat.brands.length} />
                <div className={gridClass}>
                  {cat.brands.map(brand => (
                    <BrandCard key={brand.id} brand={brand} variant={view} />
                  ))}
                </div>
              </section>
            )
          ))}
          {filteredByCat.every(c => c.brands.length === 0) && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 py-16 text-center">
              <p className="text-slate-600">No se encontraron marcas{query ? ` para “${query}”‌` : ''}.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Home;
